import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-02 (the R8 graduation).
// askBrain now sends the message + this app's declared action catalog to the generic
// fleet chat brain (/api/apps/study_companion/chat) — ONE billed call returns ONE
// registered action + a reply; the app executes it locally so data stays on-device.
// The regex intent-router below is the FREE FALLBACK (offline / signed-out / out of
// funds) — chat never goes dead. Original prototype notes:
//   • "add subject Biology"                  → local                       → add_subject
//   • "log 30 minutes of biology"            → resolve subject locally     → log_session
//   • "explain osmosis"                      → EXISTING /explain endpoint  → explain (billed, fenced)
//   • "show my plans / find notes on cells"  → local filter over library   → filter
//   • "delete <title>"                       → resolve id locally          → delete_artifact (UI confirms)
//   • anything else                          → none + a short clarifying nudge
//
// STUDY-TOOL FENCE (Creem essay-mill ban — HARD, MANDATORY pre-LLM on BOTH paths). This app
// EXPLAINS and TEACHES; it NEVER writes essays, assignments, homework answers, or submission-
// ready text. academicRefusal() runs on the raw message BEFORE serverChat is ever called — a
// do-my-homework ask is refused for FREE, so it never reaches the (billed) routing call either,
// not just the (billed) /explain call one step later (which keeps its own fence too, defence in
// depth — mirrors brain.ts::explainConcept). This mirrors the pre-graduation design, which ran
// the fence first on every message before any branch.

import { store, type ArtifactKind } from "../store";
import { BrainError, academicRefusal, brainCall } from "../brain";
import { findArtifactsByTitle, findMaterialsByTitle, type ArtifactFilter, type ChatAction, type FilterRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;        // conversational preface; executeAction supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — "digest ch.3 and ch.4" = two actions.
}

const HELP =
  "I can turn your material into study notes, build a plan, explain a concept, add subjects, log study time, search your library, and back it all up to your own GitHub or Drive. Try:\n" +
  '• "make notes from this: <paste your material>" — or tap 📷 to photograph the page\n' +
  '• "plan for my biology exam on 12 May, 6 hours a week"\n' +
  '• "explain osmosis"\n' +
  '• "add subject Biology" · "log 45 minutes of biology"\n' +
  '• "show my plans" · "find notes on cell transport"\n' +
  '• "delete Ch. 4 notes" · "back up my library"';

const none = (message: string): BrainResponse => ({ reply: "", actions: [{ kind: "none", message }] });

function pickList(rows: { title: string }[]): string {
  return rows.slice(0, 5).map((r) => `• ${r.title}`).join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) return routeMessage(text);
  // STUDY-TOOL FENCE — pre-LLM, free, on BOTH paths. A do-my-homework ask is refused
  // deterministically before it can reach EITHER billed call (routing OR explain).
  const refusal = academicRefusal(text);
  if (refusal) return none(refusal);
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return none(e.message);
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead.
    return routeMessage(text);
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (subject/title text → id) locally,
// because the data lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.study-companion.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_subject", description: "Create a new subject to study.", params: { name: "the subject's name" } },
  { name: "log_session", description: "Log a completed study session against one subject.", params: { subject: "which subject — name as the user wrote it", minutes: "how long the session was, in minutes (convert hours to minutes, e.g. 1.5 hours = 90)", note: "optional short note about the session" } },
  { name: "filter", description: "Search / list the user's study library.", params: { kind: "optional: notes, explanation, or plan", subject: "optional subject name text to filter by", text: "optional free text to search titles/content for" } },
  { name: "delete_artifact", description: "Delete one library entry (notes/explanation/plan) identified by its title (the app asks the user to confirm first).", params: { title: "which entry — title text as the user wrote it" } },
  { name: "explain", description: "Teach ONE concept: a layered explanation (analogy, precise definition, worked example, common misconception). Never writes essays, assignments, or homework answers.", params: { concept: "the concept to explain, as the user asked" } },
  // G3 chat coverage: the app's three flagship features were UI-only — the chat could not run them.
  { name: "digest", description: "Turn the user's OWN study material into structured notes (key concepts, summary, self-check questions). Use when they ask to digest / make notes from / summarise a material — either material text they paste in the message, or a material already saved in their library named by title.", params: { material_title: "optional: the title of a material already in their library", text: "optional: the material text itself, if they pasted it in the message", subject: "optional subject name it belongs to" } },
  { name: "plan", description: "Build a week-by-week study plan with spaced review, toward a goal and deadline.", params: { goal: "what they want to achieve", deadline: "the target date as YYYY-MM-DD (convert what they said, e.g. '12 May' -> 2026-05-12; if the year is unstated pick the NEXT occurrence)", subject: "optional subject name the plan belongs to", topics: "optional comma-separated topics to cover", hours_per_week: "optional number of study hours available per week (a plain number)" } },
  { name: "add_material", description: "Save a piece of study material (text they paste, or the recognized text of a page they photographed) into their library under a subject.", params: { subject: "which subject", title: "a short title for the material", text: "the material's text" } },
  { name: "rename_subject", description: "Rename an existing subject.", params: { from: "the subject's current name as the user wrote it", to: "the new name" } },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole library — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

// The server sends BOTH `actions` (the array) and back-compat `action`/`params` = the first element.
// Reading only the singular field is why "digest ch.3 and ch.4" silently did half the work.
interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const subjects = await store.listSubjects();
  const list = subjects.slice(0, 40).map((s) => s.name).join(", ");
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Subjects: ${list || "(none yet)"}.`) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent }, onChunk);

  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return none(say || HELP);   // "actions": [] = a question, a decline, or a clarification

  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    // A per-item failure ("which subject?") must not silently drop the item — surface it.
    for (const k of one.actions) if (k.kind !== "none" || raw.length === 1) actions.push(k);
    if (one.actions.some((k) => k.kind === "none") && raw.length > 1) {
      actions.push({ kind: "none", message: one.actions.find((k) => k.kind === "none")?.["message"] ?? "" });
    }
  }
  return actions.length ? { reply: say, actions } : { reply: say || HELP, actions: [{ kind: "none" }] };
}

/** Resolve a subject name the way a person would — "biolgy" finds Biology. Pattern 6. */
async function resolveSubject(term: string): Promise<{ id: string; name: string } | { ask: string }> {
  const subjects = await store.listSubjects();
  const hits = findNamedMatches(subjects, term);
  if (hits.length === 1) return { id: hits[0].id, name: hits[0].name };
  if (hits.length > 1) return { ask: `Which one — ${hits.slice(0, 3).map((s) => s.name).join(", ")}?` };
  return { ask: notFoundMessage(subjects, term) };
}

/**
 * A subject for an artifact. If they named one, resolve it. If they didn't and they only
 * HAVE one subject, use it — don't interrogate a user whose answer is unambiguous (G4).
 */
async function subjectFor(term: string | undefined): Promise<{ id: string; name: string } | { ask: string }> {
  if (term) return resolveSubject(term);
  const subjects = await store.listSubjects();
  if (subjects.length === 1) return { id: subjects[0].id, name: subjects[0].name };
  if (subjects.length === 0) return { ask: 'Add a subject first — try "add subject Biology".' };
  return { ask: `Which subject — ${subjects.slice(0, 3).map((s) => s.name).join(", ")}?` };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asMinutes = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n > 0 ? Math.max(1, Math.min(1440, Math.round(n))) : undefined;
};
const KINDS = new Set<ArtifactKind>(["notes", "explanation", "plan"]);

const one = (say: string, action: ChatAction): BrainResponse => ({ reply: say, actions: [action] });

export async function toAction(r: ServerChat): Promise<BrainResponse> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, actions: [{ kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) }] };
  }
  if (r.action === "library_list") return { reply: say, actions: [{ kind: "library_list", folder: asStr(p.folder) }] };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, actions: [{ kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` }] };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, actions: [{ kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` }] };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, actions: [{ kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") }] };
  }
  switch (r.action) {
    case "add_subject": {
      const name = asStr(p.name);
      if (!name) return none(say || 'What should the subject be called? Try "add subject Biology".');
      return one(say, { kind: "add_subject", name });
    }
    case "log_session": {
      const subjectTerm = asStr(p.subject);
      const minutes = asMinutes(p.minutes);
      if (!subjectTerm) return none(say || 'Which subject? e.g. "log 30 minutes of biology".');
      if (!minutes) return none(say || 'How long was the session? e.g. "log 30 minutes of biology".');
      return one(say, { kind: "log_session", subjectTerm, minutes, note: asStr(p.note) });
    }
    case "filter": {
      const filter: ArtifactFilter = {};
      const kindStr = asStr(p.kind)?.toLowerCase() as ArtifactKind | undefined;
      if (kindStr && KINDS.has(kindStr)) filter.kind = kindStr;
      const subject = asStr(p.subject);
      if (subject) filter.subject = subject;
      const text = asStr(p.text);
      if (text) filter.text = text;
      return one("", { kind: "filter", filter, label: say || undefined });
    }
    case "delete_artifact": {
      const term = asStr(p.title);
      if (!term) return none(say || "Which entry do you want to delete? Give me its title.");
      const rows = await findArtifactsByTitle(term);
      if (rows.length === 0) return none(`I couldn't find a library entry matching "${term}".`);
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      return one(say, { kind: "delete_artifact", id: rows[0].id, label: `"${rows[0].title}"` });
    }
    case "explain": {
      const concept = asStr(p.concept);
      if (!concept) return none(say || 'Name the concept — e.g. "explain osmosis".');
      return one(say, { kind: "explain", concept });
    }
    // ── G3: the flagship features, now reachable from the chat ────────────────
    case "digest": {
      const text = asStr(p.text);
      const title = asStr(p.material_title);
      if (text) {
        const s = await subjectFor(asStr(p.subject));
        if ("ask" in s) return none(s.ask);
        return one(say, { kind: "digest", subjectId: s.id, text, title });
      }
      if (!title) return none(say || "Paste the material, photograph the page, or name a material in your library to digest.");
      const mats = await findMaterialsByTitle(title);
      if (mats.length === 0) return none(`I couldn't find a material called "${title}" in your library.`);
      if (mats.length > 1) return none(`I found ${mats.length} materials matching "${title}" — which one?\n${pickList(mats)}`);
      // Reuse the saved material's own subject; never re-ask for something we already know (G4).
      return one(say, { kind: "digest", subjectId: mats[0].subject_id, text: mats[0].content, title: mats[0].title, materialId: mats[0].id });
    }
    case "plan": {
      const goal = asStr(p.goal);
      if (!goal) return none(say || 'What are you working toward? e.g. "plan for my biology exam on 12 May".');
      const s = await subjectFor(asStr(p.subject));
      if ("ask" in s) return none(s.ask);
      return one(say, {
        kind: "plan", subjectId: s.id, goal, deadline: asStr(p.deadline),
        topics: asStr(p.topics), hoursPerWeek: asMinutes(p.hours_per_week),
      });
    }
    case "add_material": {
      const text = asStr(p.text);
      const subjectTerm = asStr(p.subject);
      if (!text) return none(say || "What's the material? Paste it, or photograph the page with the camera button.");
      if (!subjectTerm) return none(say || "Which subject should I file it under?");
      const s = await resolveSubject(subjectTerm);
      if ("ask" in s) return none(s.ask);
      return one(say, { kind: "add_material", subjectId: s.id, subjectName: s.name, title: asStr(p.title) ?? "Material", text });
    }
    case "rename_subject": {
      const from = asStr(p.from), to = asStr(p.to);
      if (!from || !to) return none(say || 'Tell me both names — e.g. "rename Bio to Biology".');
      const s = await resolveSubject(from);
      if ("ask" in s) return none(s.ask);
      return one(say, { kind: "rename_subject", id: s.id, from: s.name, to });
    }
    case "backup_save":
      return one(say, { kind: "backup_save" });
    case "backup_status":
      return one(say, { kind: "backup_status" });
    case "backup_restore": {
      const prov = asStr(p.provider);
      return one(say, { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined });
    }
    case "save_local_copy":
      return one(say, { kind: "save_local_copy" });
    case "import_file":
      return one(say, { kind: "import_file" });
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return one(say, { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination });
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, actions: [{ kind: "remember", instruction }] };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 0) The study-tool fence runs FIRST — a do-my-homework ask never reaches any branch.
  const refusal = academicRefusal(t);
  if (refusal) return none(refusal);

  // 1) ADD SUBJECT — local (no server call).
  const subM = t.match(/^(?:add|create|new)\s+(?:a\s+)?subject[:\-\s]+([\s\S]+)$/i);
  if (subM) {
    const name = subM[1].trim().replace(/[.?!]+$/, "");
    if (!name) return none('What should the subject be called? Try "add subject Biology".');
    return { reply: "", actions: [{ kind: "add_subject", name }] };
  }

  // 2) LOG A SESSION — "log 30 minutes of biology" / "studied biology for 1 hour".
  let logM = t.match(/^(?:log|record|add)\s+(\d+)\s*(?:min(?:ute)?s?|m)\s+(?:of\s+|on\s+|for\s+)?(.+)$/i)
    || t.match(/^(?:log|record|add)\s+([\d.]+)\s*(?:hours?|hrs?|h)\s+(?:of\s+|on\s+|for\s+)?(.+)$/i);
  if (logM) {
    const isHours = /hours?|hrs?|\dh|\sh\b/i.test(logM[0].split(logM[2])[0] ?? "");
    const n = Number(logM[1]);
    const minutes = Math.round(isHours ? n * 60 : n);
    const subjectTerm = logM[2].trim().replace(/[.?!]+$/, "");
    if (!Number.isFinite(minutes) || minutes < 1) return none("How long was the session? e.g. \"log 30 minutes of biology\".");
    return { reply: "", actions: [{ kind: "log_session", subjectTerm, minutes }] };
  }
  logM = t.match(/^(?:i\s+)?studied\s+(.+?)\s+for\s+(\d+)\s*(min(?:ute)?s?|m|hours?|hrs?|h)\b/i);
  if (logM) {
    const n = Number(logM[2]);
    const minutes = /h/i.test(logM[3]) ? Math.round(n * 60) : Math.round(n);
    return { reply: "", actions: [{ kind: "log_session", subjectTerm: logM[1].trim(), minutes }] };
  }

  // 3) EXPLAIN — the billed step (EXISTING /explain endpoint, already fenced above).
  const expM = t.match(/^(?:explain|what\s+is|what\s+are|what'?s|teach\s+me)\s+(.+)$/i);
  if (expM) {
    const concept = expM[1].trim().replace(/[.?!]+$/, "").replace(/^(?:the\s+concept\s+of\s+)/i, "");
    if (!concept) return none('Name the concept — e.g. "explain osmosis".');
    return { reply: "", actions: [{ kind: "explain", concept }] };
  }

  // 4) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove|trash)\s+(?:the\s+)?(?:entry\s+|notes?\s+|plan\s+|explanation\s+)?(?:for\s+|called\s+)?([\s\S]+)$/i);
  if (delM) {
    const term = delM[1].trim();
    const rows = await findArtifactsByTitle(term);
    if (rows.length === 0) return none(`I couldn't find a library entry matching "${term}".`);
    if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
    return { reply: "", actions: [{ kind: "delete_artifact", id: rows[0].id, label: `"${rows[0].title}"` }] };
  }

  // 5) FILTER / SEARCH — query-shaped → local filter over the on-device library.
  if (/\b(show|find|list|search|which|filter)\b/i.test(low)) {
    const filter = parseFilter(t);
    return { reply: "", actions: [{ kind: "filter", filter, label: t }] };
  }

  // 6) Unclear → a short clarifying nudge.
  return none(HELP);
}

/** Build a local ArtifactFilter from free text — kind, subject, free text. */
function parseFilter(text: string): ArtifactFilter {
  const low = text.toLowerCase();
  const f: ArtifactFilter = {};
  const kinds: [RegExp, ArtifactKind][] = [
    [/\bplans?\b/i, "plan"],
    [/\bexplanations?\b/i, "explanation"],
    [/\bnotes?\b/i, "notes"],
  ];
  for (const [re, k] of kinds) {
    if (re.test(low)) { f.kind = k; break; }
  }
  const inM = text.match(/\b(?:in|for|under)\s+([a-z][\w -]*)$/i);
  if (inM) f.subject = inM[1].trim();
  const onM = text.match(/\b(?:about|on|containing|mentioning)\s+(.+)$/i);
  if (onM) f.text = onM[1].trim().replace(/[.?!]+$/, "");
  return f;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
